import { createContext, useState, useCallback, useMemo } from 'react';
import type { ReactNode, FunctionComponent, Dispatch, SetStateAction } from 'react';

import { createUID, hasProp, useAfterInitialEffect } from '@pega/cosmos-react-core';
import type { FileUploadItemProps, FileItemProps, OmitStrict } from '@pega/cosmos-react-core';

// -----------------------------------------------------------------------------
// MockFileResourceObject
// -----------------------------------------------------------------------------
export interface MockFileAttachmentResource {
  id: string;
  name: string;
  category: string;
  thumbnail?: string;
  createDate?: number;
  modifiedDate?: number;
}

// -----------------------------------------------------------------------------
// MockFileSelectionObject
// -----------------------------------------------------------------------------
export interface MockFileUploadItem
  extends Pick<MockFileAttachmentResource, 'id' | 'category'>,
    OmitStrict<FileUploadItemProps, 'id'> {
  File: File;
}

export interface MockFileItem
  extends OmitStrict<FileItemProps, 'id'>,
    Pick<MockFileAttachmentResource, 'id' | 'category'> {
  File: File;
}

// -----------------------------------------------------------------------------
// FileServiceContextValue
// -----------------------------------------------------------------------------
export interface FileServiceContextValue {
  files: (MockFileAttachmentResource | MockFileUploadItem | MockFileItem)[];
  setFiles: Dispatch<SetStateAction<FileServiceContextValue['files']>>;
  attachFile: (file: MockFileItem) => void;
  cancelFile: (id: MockFileAttachmentResource['id']) => void;
  deleteFile: (id: MockFileAttachmentResource['id']) => void;
}

// -----------------------------------------------------------------------------
// FileServiceContext
// -----------------------------------------------------------------------------
export const FileServiceContext = createContext<FileServiceContextValue>({
  files: [],
  setFiles: () => {},
  attachFile: () => {},
  cancelFile: () => {},
  deleteFile: () => {}
});

// -----------------------------------------------------------------------------
// FilePostArg
// -----------------------------------------------------------------------------
interface FilePostArg {
  file: MockFileUploadItem | MockFileItem;
  onProgress: (file: MockFileUploadItem | MockFileItem, progress: number) => void;
  onLoad: (file: MockFileUploadItem | MockFileItem) => void;
  currentProgress: number;
}

// -----------------------------------------------------------------------------
// postFile
// -----------------------------------------------------------------------------
const postFile = ({ file, onProgress, onLoad, currentProgress }: FilePostArg) => {
  onProgress(file, currentProgress);
  setTimeout(() => {
    const progress = currentProgress + 5;
    if (progress <= 100) postFile({ file, onProgress, onLoad, currentProgress: progress });
    else onLoad(file);
  }, 100);
};

// Mock date generator
const getMockPriorDate = (h: number) => {
  const d = new Date();
  return d.setHours(d.getHours() - h);
};

export const mockCategories: ('HR' | 'Marketing' | 'Sales' | 'Accounting' | 'Engineering')[] = [
  'HR',
  'Marketing',
  'Sales',
  'Accounting',
  'Engineering'
];

export const mockAttachmentListRequest = (): Promise<MockFileAttachmentResource[]> => {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve([
        {
          id: createUID(),
          name: 'File.doc',
          category: mockCategories[0],
          createDate: getMockPriorDate(1)
        },
        {
          id: createUID(),
          name: 'File.ppt',
          category: mockCategories[1],
          createDate: getMockPriorDate(4)
        },
        {
          id: createUID(),
          name: 'File.xls',
          category: mockCategories[2],
          createDate: getMockPriorDate(30)
        },
        {
          id: createUID(),
          name: 'emptyFile',
          category: '',
          createDate: getMockPriorDate(144)
        }
      ]);
    }, 1000);
  });
};

// -----------------------------------------------------------------------------
// Provider
// -----------------------------------------------------------------------------
const Provider: FunctionComponent<{ children: ReactNode }> = ({ children }) => {
  const [files, setFiles] = useState<FileServiceContextValue['files']>([]);

  const attachFile = useCallback(
    (file: MockFileUploadItem | MockFileItem) => {
      if (files.some(f => f.id === file.id)) return;
      setFiles(current => [file, ...current]);
    },
    [files]
  );

  // Remove a file from the state array
  const cancelFile = (id: MockFileUploadItem['id'] | MockFileItem['id']) => {
    setFiles(current => current.filter(f => f.id !== id));
  };

  // Mock an api request and remove a file from the state array
  const deleteFile = useCallback(
    async (id: MockFileUploadItem['id']) => {
      const file = files.find(f => f.id === id) as MockFileAttachmentResource;
      setFiles(current => current.filter(f => f.id !== file.id));
    },
    [files]
  );

  const setProgress = useCallback((file: MockFileUploadItem | MockFileItem, progress?: number) => {
    setFiles(current =>
      current.map(f => {
        if (file.id === f.id) {
          (f as MockFileUploadItem | MockFileItem).progress = progress;
        }
        return f;
      })
    );
  }, []);

  useAfterInitialEffect(() => {
    files.forEach(file => {
      // Return if we have a mock attachment resource object or a File object that is currently uploading.
      if (!hasProp(file, 'File') || typeof file.progress === 'number') return;

      // For each File in the array, not uploaded yet, mock the upload progress.
      postFile({
        file,
        onProgress: setProgress,
        currentProgress: 0,
        onLoad(loadedFile) {
          setFiles(cur => {
            return cur.map(f => {
              if (f.id === loadedFile.id) {
                return {
                  ...loadedFile,
                  createDate: Date.now()
                };
              }

              return f;
            });
          });
        }
      });
    });
  }, [files.length, setProgress]);

  const value: FileServiceContextValue = useMemo(
    () => ({
      files,
      setFiles,
      attachFile,
      cancelFile,
      deleteFile
    }),
    [files, attachFile, cancelFile, deleteFile]
  );

  return <FileServiceContext.Provider value={value}>{children}</FileServiceContext.Provider>;
};

export default Provider;
